import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import withErrorBoundary from 'src/components/errorBoundary/withErrorBoundary';

import DealHeader from './components/dealHeader';
import DealTimeline from './components/dealTimeline';
import DealActivity from '../components/dealActivity';
import DealContacts from './components/dealContacts';
import ContentShared from './components/contentShared';
import Nudges from './components/nudges';

import styles from './styles.module.scss';
import Actions from 'src/components/actionbar';
import { useDealDetail, useSyncDeal } from 'src/query/dealInsights/dealInsights';
import { toast } from "react-toastify";
import Toast from "src/components/toast";
import { useQueryClient } from '@tanstack/react-query';
import { DEAL_CHAT, DEAL_DETAILS, DEAL_INSIGHTS, DEAL_INSIGHTS_ACTIVITIES, INVALIDATE } from 'src/query/queryKeys';
import DealAgent from '../components/dealAgent';

import { useIntegrationsQuery } from 'src/query/settings/integrations';
import { size } from 'lodash';
import Button from 'src/components/button';
import EmptyMessage from 'src/components/emptyMessage';

const emptyDealsImg = '/images/dealInsights/emptyDeals.svg';

export const DealDetailsView = () => {

  const navigate = useNavigate();
  const { id } = useParams(); // get the deal id
  const queryClient = useQueryClient();

  // query's
  const { data: dealDetails, isLoading: isLoadingDealDetails, isError: isDealDetailsError, refetch: refetchDealDetails, isRefetching: isDealDetailsRefetching } = useDealDetail(id); // deal details
  const { mutate: syncDeal, isSuccess: isSyncSuccess, isPending: isSyncPending, } = useSyncDeal(); // sync 
  const { data: integrations, isLoading: isLoadingIntegrations } = useIntegrationsQuery(); // integrations
  const [hubspotPortalId, setHubspotPortalId] = useState<string | null>(null);
  const location = useLocation();


  useEffect(() => {
    if (size(integrations) > 0) {
      const hubspotIntegration = integrations.find((integration: any) => integration.type === 'hubspot');
      if (hubspotIntegration) {
        setHubspotPortalId(hubspotIntegration?.configuration?.portalId ?? null);
      }
    }
  }, [integrations]);

  // Methods
  useEffect(() => {
    if (isSyncSuccess) {
      toast(
        <Toast.Sucess
          message="Sucessfully Synced"
        />, {
        autoClose: 3000,
      })
    }
  }, [isSyncSuccess]);

  const handleBack = () => { // go back
    navigate('/deal-insights');
    const backUrl = location.state?.from ? `/deal-insights${location.state.from}` : '/deal-insights';
    navigate(backUrl);
  };

  if (isDealDetailsError && !isLoadingDealDetails && !dealDetails) { // If the deal details are not found, show the error state
    return (
      <div className={cx(styles.container, styles.errorState)}>
        <div className={styles.leftPane}>
          <Button className={styles.backButton} onClick={handleBack}>
            <i className="paperflite-left-arrow" /> back to  <span className={styles.highlight}> &quot;deals&quot; </span>
          </Button>
        </div>
        <div className={styles.emptyContainer}>
          <EmptyMessage
            width={450}
            heading="Sorry! The deal you're looking for doesn't exist"
            image={emptyDealsImg}
          />
        </div>
      </div>
    );
  }

  return (
      <div className={styles.container}>
        <div className={styles.leftPane}>
          <Button className={styles.backButton} onClick={handleBack}>
            <i className="paperflite-left-arrow" /> back to  <span className={styles.highlight}> &quot;deals&quot; </span>
          </Button>
           <div className={styles.content}> 
            <DealAgent loadingSummary={isLoadingDealDetails} summary={dealDetails?.dealSummary ?? null} dealId={id ?? ''} />
          </div>
        </div>
        <div className={styles.rightPane}>
          <div className={styles.syncStatus}>
            <p className={styles.content}>{isSyncPending ? 'Syncing' : 'Last synced today'}</p>
            <Actions.IconButton
              icon="paperflite-reverse-curved"
              onClick={() => { !isSyncPending && syncDeal(dealDetails?.externalId ?? '') }}
              className={isSyncPending ? styles.icon : ''}
            />
          </div> 
          <div className={styles.content}>
            <DealHeader deal={dealDetails} isLoading={isLoadingDealDetails || isDealDetailsRefetching} portalId={hubspotPortalId} />
            <DealTimeline dealId={dealDetails?.externalId ?? ''} />
            <Nudges dealId={dealDetails?.externalId ?? ''} />
            <DealActivity isDetailedView={true} dealId={dealDetails?.externalId ?? ''} title={'Deal Activity'} />
            <DealContacts contacts={dealDetails?.contacts || []} isLoading={isLoadingDealDetails || isDealDetailsRefetching} />
            {/* <ContentShared items={mockData.contentShared as any} /> */}
            </div>
        </div>
      </div>
  );
};

export default withErrorBoundary(DealDetailsView);
